import React, { useEffect, useMemo } from 'react'
import styles from 'modules/Services/CampaignsEditor/campaigns-editor.module.scss'
import PropTypes from 'prop-types'
import { Button, Container, Grid, Icon, Typography } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import RESOURCES from 'modules/Services/CampaignsEditor/resourcesMap'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useParams } from 'react-router'
import ColumnSwitcher from 'modules/Services/CampaignsEditor/subcomponents/ColumnSwitcher'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import ActionDropdown from 'modules/Services/CampaignsEditor/subcomponents/ActionsDropdown'
import XlsDownloader from 'modules/Services/CampaignsEditor/subcomponents/XlsDownloader'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import {
  DROP_DATA,
  FETCH_ADGROUPS_COUNTER,
  FETCH_BANNERS_COUNTER,
  SET_COUNTER,
  SET_SELECTION,
} from 'modules/Services/CampaignsEditor/types'

const ActionBar = ({
  hasItems = false,
  resource,
  className = '',
  tableState = null,
  selectedStatuses = [],
}) => {
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const { networkType } = useParams()
  const breakpoint = useBreakpoint()
  const dispatch = useDispatch()

  const { counters, selections, currentListSelectionIds, items } = useSelector(
    (state) => ({
      counters: state['services/campaigns-editor'].counters,
      selections: {
        campaigns: state['services/campaigns-editor'].campaigns.selection.ids,
        'ad-groups':
          state['services/campaigns-editor']['ad-groups'].selection.ids,
        banners: state['services/campaigns-editor'].banners.selection.ids,
      },
      currentListSelectionIds:
        state['services/campaigns-editor']?.[resource].selection.ids,
      items: state['services/campaigns-editor']?.[resource].items,
    }),
  )

  const currentListSelectedItems = useMemo(
    () =>
      items.filter((item) =>
        Object.keys(currentListSelectionIds)
          .map((key) => +key)
          .includes(+item.external_id),
      ),
    [currentListSelectionIds, items],
  )

  useEffect(() => {
    dispatch({
      type: SET_SELECTION,
      payload: { resource: 'ad-groups', ids: {} },
    })
    
    dispatch({
      type: SET_SELECTION,
      payload: { resource: 'banners', ids: {} },
    })

    if (Object.keys(selections.campaigns).length === 0) {
      dispatch({ type: SET_COUNTER, payload: { 'ad-groups': 0, banners: 0 } })
    } else {
      dispatch({ type: FETCH_ADGROUPS_COUNTER })
      dispatch({ type: FETCH_BANNERS_COUNTER })
    }

    dispatch({ type: DROP_DATA, payload: { resource: 'ad-groups' } })
    dispatch({ type: DROP_DATA, payload: { resource: 'banners' } })
  }, [selections.campaigns])

  useEffect(() => {
    dispatch({
      type: SET_SELECTION,
      payload: { resource: 'banners', ids: {} },
    })

    if (Object.keys(selections['ad-groups']).length === 0) {
      dispatch({ type: SET_COUNTER, payload: { banners: 0 } })
    } else {
      dispatch({ type: FETCH_BANNERS_COUNTER })
    }
    dispatch({ type: DROP_DATA, payload: { resource: 'banners' } })
  }, [selections['ad-groups']])

  const availableActions = useMemo(
    () => RESOURCES[resource].actions(selectedStatuses),
    [resource, selectedStatuses],
  )

  return (
    <>
      <Container
        display={'flex'}
        flexJustify={'space-between'}
        className={className}
      >
        <Grid gap={16} alignItems={'center'} wrap={'nowrap'}>
          <Container className={styles['buttons-group']}>
            {Object.keys(RESOURCES).map((resourceName) => (
              <Button
                noWrap
                className={styles['buttons-group__button']}
                component={Link}
                replace={true}
                to={pathGen(RESOURCES[resourceName].route, {
                  networkType,
                  resource: resourceName,
                })}
                variant={resource === resourceName ? 'primary' : 'ghost'}
                size={'s'}
                key={'switcher-to-' + resourceName}
                noHover
                color={
                  resource === resourceName
                    ? 'Label-Onsurface'
                    : 'Label-Secondary'
                }
                bgColorHover={'none'}
                endIcon={
                  Object.keys(selections[resourceName])?.length ? (
                    <Grid
                      gap={4}
                      alignItems={'center'}
                      wrap={'nowrap'}
                      flexDirection={'row'}
                      fluid={false}
                    >
                      <Container display={'flex'}>
                        <Typography
                          variant={'Body-Forms-20'}
                          className={clsx(styles['buttons-group__counter'], {
                            [styles['buttons-group__counter_focused']]:
                              resource === resourceName,
                          })}
                        >
                          {Object.keys(selections[resourceName])?.length}
                        </Typography>
                      </Container>
                      <Container display={'flex'}>
                        <span
                          onClick={(e) => {
                            e.preventDefault()

                            dispatch({
                              type: SET_SELECTION,
                              payload: { resource: resourceName, ids: {} },
                            })
                          }}
                        >
                          <Icon name={'close'} size={'m'} width={20} />
                        </span>
                      </Container>
                    </Grid>
                  ) : counters[resourceName] ? (
                    <Typography
                      variant={'Body-Forms-20'}
                      className={clsx(styles['buttons-group__counter'], {
                        [styles['buttons-group__counter_focused']]:
                          resource === resourceName,
                      })}
                    >
                      {counters[resourceName]}
                    </Typography>
                  ) : null
                }
              >
                {t('campaigns_editor::resources.' + resourceName)}
              </Button>
            ))}
          </Container>

          {!breakpoint.sm && (
            <ActionDropdown
              resource={resource}
              noLabel={breakpoint.fb && !breakpoint.sm}
              actions={availableActions}
              currentListSelectionIds={currentListSelectionIds}
              currentListSelectedItems={currentListSelectedItems}
            />
          )}

          {/*скрываем до лучших времен*/}
          {/*{!breakpoint.sm && (*/}
          {/*  <Tooltip*/}
          {/*    align={'center'}*/}
          {/*    title={t('campaigns_editor::refresh_button.label')}*/}
          {/*    placement="top"*/}
          {/*    leaveDelay={250}*/}
          {/*    arrow*/}
          {/*  >*/}
          {/*    <Button*/}
          {/*      onClick={refreshStatistics}*/}
          {/*      size={'m'}*/}
          {/*      variant={'secondary'}*/}
          {/*      noLabel*/}
          {/*      startIcon={<Icon name={'rotate'} />}*/}
          {/*      grey*/}
          {/*      borderColor={'none'}*/}
          {/*      borderColorHover={'none'}*/}
          {/*      borderColorActive={'none'}*/}
          {/*    />*/}
          {/*  </Tooltip>*/}
          {/*)}*/}
        </Grid>

        {!breakpoint.sm && (
          <Grid
            gap={16}
            alignItems={'center'}
            flexJustify={'flex-end'}
            wrap={'nowrap'}
          >
            <ColumnSwitcher
              tableState={tableState}
              buttonProps={{ noLabel: breakpoint.lg, disabled: !hasItems }}
            />
            <XlsDownloader noLabel={breakpoint.lg} resource={resource} />
          </Grid>
        )}
      </Container>

      {breakpoint.sm && (
        <Container flexJustify={'space-between'} display={'flex'}>
          <Grid wrap={'nowrap'} fluid={false} gap={16} alignItems={'center'}>
            <ActionDropdown
              size={'s'}
              resource={resource}
              noLabel={breakpoint.fb && !breakpoint.sm}
              actions={availableActions}
              currentListSelectionIds={currentListSelectionIds}
            />
            {/*скрываем до лучших времен*/}
            {/*<Button*/}
            {/*  onClick={refreshStatistics}*/}
            {/*  size={'s'}*/}
            {/*  variant={'secondary'}*/}
            {/*  startIcon={<Icon name={'rotate'} />}*/}
            {/*  grey*/}
            {/*  borderColor={'none'}*/}
            {/*  borderColorHover={'none'}*/}
            {/*  borderColorActive={'none'}*/}
            {/*/>*/}
          </Grid>
          <Grid wrap={'nowrap'} gap={16} fluid={false} alignItems={'center'}>
            <ColumnSwitcher
              tableState={tableState}
              buttonProps={{
                noLabel: breakpoint.sm,
                size: 's',
                disabled: !hasItems,
              }}
            />
            <XlsDownloader noLabel={true} size={'s'} resource={resource} />
          </Grid>
        </Container>
      )}
    </>
  )
}

ActionBar.propTypes = {
  hasItems: PropTypes.bool,
  resource: PropTypes.string,
  refreshStatistics: PropTypes.func,
  className: PropTypes.string,
  tableState: PropTypes.object,
  sortingParams: PropTypes.object,
  selectedStatuses: PropTypes.array,
}

export default ActionBar
